import {ObjUtil} from 'yougile-util'
import {
  DataObjectId,
  getGTDLastChangeTime,
  getGTDStage,
  Hub,
  NotificationSettings,
  repeatTemplatesContainerId,
  SystemMessageUtils,
  User,
  youGileUserId
} from 'yougile-model'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {UserSettings} from '@client/user-settings'
import {FilePreview} from '@common/file-preview'
import {getUnseenMentions, getUnseenMessages} from '@extensions/chat'
import {ChatSettings} from '@extensions/chat-settings'
import {globals} from '@extensions/globals-base'
import {MessageUtils} from '@extensions/message-utils'
import {ChatMessageUtils} from '../chat-message-utils'
import {KindOfTaskParticipation} from '../chat-types'
import {ChatUtils} from '../chat-utils'
import {NotificationSettingsService} from '../notifications-settings-service'
import {ChatProxy} from './chat-proxy'

let oldSettings: NotificationSettings

export type TaskChatProxy = ChatProxy & {
  readonly companyId: DataObjectId
  readonly kindOfTaskParticipation: KindOfTaskParticipation
  readonly complete?: number
  readonly doneAt?: number
  starred: boolean
  updateTime: number
}

export interface RankedTask {
  task: TaskChatProxy
  archive: number
  starred: number
  unread: number
  time: number
  markImportant: number
}

export class TaskChatProxyService {
  static chatProxyCache: Map<DataObjectId, Map<DataObjectId, TaskChatProxy>> =
    new Map()

  static deleteCache() {
    this.chatProxyCache.clear()
  }

  static initCache() {
    // cleaning cache once in 10 minutes
    // we don't want to warry about cleaning cache manually
    setInterval(() => {
      this.chatProxyCache.clear()
    }, 10 * 60_000)

    // updating on company settings change
    let savedSettings = UserSettings.getForCurrentCompany()
    SyncEvents.addListener(Sync.profileId, '*', () => {
      const settings = UserSettings.getForCurrentCompany()
      if (!ObjUtil.deepEqual(savedSettings, settings, false)) {
        savedSettings = settings
        TaskChatProxyService.deleteCache()
      }
    })
  }

  static create(
    user: User,
    hub: Hub,
    chatHubsWithCurrentUser?: Hub[]
  ): TaskChatProxy {
    const notificationSettings =
      NotificationSettingsService.getNotificationSettings()
    const {authorRealNameForDisplay} = ChatMessageUtils
    const {getChatLocation} = ChatUtils
    const {chat, companyId} = hub
    const settings = ChatSettings.getSettings()
    const lastMessage = chat.lastMessage({
      forUser: globals.currentUserId,
      ignoreReadSystemMessages: !settings.showReadSystemMessages,
      additionalFilter: NotificationSettingsService.getTaskMessageFilter(
        notificationSettings,
        hub
      )
    })
    const lastMessageFromMeTimestamp = chat.lastMessage({
      userId: globals.currentUserId
    })?.timestamp

    if (!this.chatProxyCache.has(companyId)) {
      this.chatProxyCache.set(companyId, new Map())
    }
    const companyCache = this.chatProxyCache.get(companyId)
    const cached = companyCache?.get(hub.id)
    if (
      cached &&
      cached.updateTime === hub.updateTime &&
      typeof oldSettings !== 'undefined' &&
      typeof notificationSettings !== 'undefined' &&
      oldSettings === notificationSettings
    ) {
      return cached
    }

    const userId = user.id

    const lastMessageTimestamp = lastMessage ? lastMessage.timestamp : 0
    const lastMessageText = lastMessage
      ? FilePreview.encodedToFilenames(
          SystemMessageUtils.isSystemMessage(lastMessage)
            ? MessageUtils.systemMessageText(lastMessage)
            : lastMessage.text
        )
      : undefined

    const lastMessageAuthor = lastMessage
      ? authorRealNameForDisplay(lastMessage, hub)
      : undefined

    const kindOfTaskParticipation = chatHubsWithCurrentUser
      ? chatHubsWithCurrentUser.includes(hub)
        ? 'withCurrentUser'
        : 'withoutCurrentUser'
      : ChatUtils.kindOfTaskParticipation(Sync.get(hub), hub)

    const stage = getGTDStage(hub.data.gtd)
    const chatName =
      hub.location === repeatTemplatesContainerId
        ? hub.data?.repeatTemplate?.title
        : hub.companyId !== Sync.profileId && hub.properties.feedbackUserEmail
          ? '📢 ' + hub.properties.feedbackUserEmail + ' ➡️ ' + hub.title
          : hub.title

    const result: TaskChatProxy = {
      companyId,
      chatId: hub.id,
      createdTimestamp: hub.timestamp,
      updateTime: hub.updateTime,
      chatType: 'task',
      chatName,
      newMessagesAmount: getUnseenMessages({
        userId,
        hub,
        startFrom: user.timestamp,
        includeSystem: true,
        notificationSettings
      }),
      getNewMentionsAmount: startFrom =>
        getUnseenMentions({userId, hub, startFrom}),

      markImportant: hub.data?.markImportantMap?.[globals.currentUserId],
      markAsSeen: () => hub && ChatUtils.markAsSeen(hub),
      lastMessageFromMeTimestamp,
      starred: UserSettings.isStarred(hub),
      messagesCount: chat.length,
      kindOfTaskParticipation,
      locationPath: getChatLocation(hub),
      lastMessageText,
      lastMessageTimestamp,
      lastMessageAuthor:
        lastMessage && lastMessage.from === youGileUserId
          ? ''
          : lastMessageAuthor,
      lastMessageId: lastMessage?.id,
      complete: hub.complete,
      doneAt: stage === 'done' ? getGTDLastChangeTime(hub.data.gtd) : undefined
    }

    companyCache?.set(hub.id, result)

    return result
  }

  static createForCurrentUser(
    hub: Hub,
    chatHubsWithCurrentUser?: Hub[]
  ): TaskChatProxy {
    return TaskChatProxyService.create(
      Sync.getCurrentUser(),
      hub,
      chatHubsWithCurrentUser
    )
  }
}
